import React, { useEffect, useState } from "react";
import { PriceMotionStyle, BillingToggle, Rolling } from "./PriceMotion";
import { Check, Minus, ArrowRight, ChevronDown } from "lucide-react";
import { foundingEndText, foundingOpen, money, savingPercent, usePricing } from "./pricing";
import { track } from "./analytics";

// The public pricing page (/pricing). Sections follow the brief: plans, extras price list, comparison, questions, closing band.
// Prices, the founding window and every limit come from the gateway's config; only words are written here.

const cardBase = "relative flex flex-col rounded-2xl border p-6";
const eyebrow = "text-[11px] font-bold uppercase tracking-[0.18em]";

function featuresOf(cfg) {
  const ai = cfg.addOns.aiTests.includedPerMonth;
  return {
    noor: ["Read Qur'an and Tajweed", "Understand Qur'an Course 1", "Basic Hifz with self-test", "Quran Fluency and 85% Words"],
    naim: ["Everything in Noor", "Children Poems (ages 3–7), then Asma ul Husna and Seerah", `${cfg.addOns.stories.includedPerYear} Qur'anic Stories a year (ages 8–13)`,
      `Hifz: Juz 'Amma, ${ai} AI tests a month`],
    adult: ["Everything in Noor", "Quran Fluency and 85% Words, beyond the free year", "Understanding Surah Al-Baqarah · Juz 1 (coming soon)", "Living with the Qur'an (coming soon)",
      `Hifz: Juz 'Amma, ${ai} AI tests a month`, "Hifz: Important Surahs", "Hifz: Surah Al-Baqarah"],
    family: ["Everything in Na'im and 'Adn · Firdaws", "Up to 5 profiles: father, mother, children, grandparent", `${ai} AI Hifz tests a month for each member`, "Family dashboard"],
  };
}

function PlanCard({ k, cfg, billing, features, onPick }) {
  const p = cfg.plans[k];
  const open = foundingOpen(cfg);
  const yearly = billing === "yearly";
  const dark = k === "family";
  const muted = dark ? "text-sand-100/75" : "text-foreground/65";
  const href = `/checkout?plan=${k}&billing=${billing}`;
  const saveStd = p.monthly ? savingPercent(p.monthly, p.yearly) : 0;
  const saveFounding = p.monthly ? savingPercent(p.monthly, p.foundingYearly) : 0;
  const cta = k === "noor" ? "Start Free" : k === "family" ? "Choose Family" : `Choose ${p.name}`;
  return (
    <article className={`${cardBase} ${dark ? "bg-forest-700 border-forest-700 text-sand-100" : "bg-white border-sand-300"}`} data-testid={`price-card-${k}`}>
      {dark && <span className="absolute -top-3 left-6 rounded-full bg-[#DEF5E5] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-forest-700">Best value</span>}
      <div className={`${eyebrow} ${dark ? "text-gold-400" : "text-gold-600"}`}>{p.stage || "Free to begin"}</div>
      <h3 className="mt-2 font-display text-2xl font-semibold">{p.name}</h3>
      {p.arabic && <div lang="ar" dir="rtl" className="mt-0.5 text-left font-arabic text-lg opacity-80">{p.arabic}</div>}
      <div key={billing} className={`mt-4 min-h-[8.5rem] ${k === "noor" ? "" : "price-swap"}`}>
        {k === "noor" ? (
          <>
            <div><span className="font-display text-5xl font-semibold tracking-tight">$0</span> <span className={`text-sm ${muted}`}>/ first year</span></div>
            <p className={`mt-2 text-sm ${muted}`}>Then {money(p.renewYearly)} a year to keep it</p>
          </>
        ) : yearly && open ? (
          <>
            <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${dark ? "bg-[#DEF5E5] text-forest-700" : "bg-forest-600/10 text-forest-700"}`}>Founding price · locked in</span>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="font-display text-5xl font-semibold tracking-tight" data-testid={`price-${k}`}><Rolling text={money(p.foundingYearly)} /></span>
              <s className={`text-base ${muted}`} data-testid={`standard-${k}`}><Rolling text={money(p.yearly)} /></s>
              <span className={`text-sm ${muted}`}>/ year</span>
            </div>
            <p className={`mt-2 text-sm ${muted}`}>Locked while you stay subscribed. Standard price {money(p.yearly)}.</p>
          </>
        ) : yearly ? (
          <>
            <div className="flex items-baseline gap-2"><span className="font-display text-5xl font-semibold tracking-tight" data-testid={`price-${k}`}><Rolling text={money(p.yearly)} /></span><span className={`text-sm ${muted}`}>/ year</span></div>
            <p className="mt-2"><span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${dark ? "bg-[#DEF5E5] text-forest-700" : "bg-forest-600/10 text-forest-700"}`}>Save {saveStd}%</span></p>
          </>
        ) : (
          <>
            <div className="flex items-baseline gap-2"><span className="font-display text-5xl font-semibold tracking-tight" data-testid={`price-${k}`}><Rolling text={money(p.monthly)} /></span><span className={`text-sm ${muted}`}>/ month</span></div>
            <p className={`mt-2 text-sm ${muted}`}>{open ? `Or ${money(p.foundingYearly)} a year at the founding price, save ${saveFounding}%` : `Or ${money(p.yearly)} a year, save ${saveStd}%`}</p>
          </>
        )}
      </div>
      <ul className="mt-5 flex-1 space-y-2.5 border-t pt-5 text-sm" style={{ borderColor: dark ? "#3E6B5A" : undefined }}>
        {features[k].map((f) => (
          <li key={f} className="flex gap-2.5"><Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-[#B6D9BD]" : "text-forest-600"}`} aria-hidden="true" />{f}</li>))}
      </ul>
      <a href={k === "noor" ? "/login?mode=register" : href} onClick={() => onPick(k)} data-testid={`price-cta-${k}`}
        className={`mt-6 inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[10px] px-5 py-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500 focus-visible:ring-offset-2 ${
          dark ? "bg-[#DEF5E5] text-forest-700 hover:bg-white" : k === "noor" ? "border border-forest-600/40 text-forest-700 hover:bg-forest-600/5" : "bg-forest-700 text-sand-100 hover:bg-forest-800"}`}>
        {cta} <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </a>
    </article>
  );
}

function Mark({ v }) {
  if (v === true) return <><Check className="inline h-4 w-4 text-forest-600" aria-hidden="true" /><span className="sr-only">Included</span></>;
  if (v === false) return <><Minus className="inline h-4 w-4 text-foreground/30" aria-hidden="true" /><span className="sr-only">Not included</span></>;
  return <span className="text-xs">{v}</span>;
}

export default function PricingPage() {
  const { cfg, error } = usePricing();
  const [billing, setBilling] = useState("yearly");
  useEffect(() => { track("pricing_viewed", {}); }, []);
  if (error) return <div className="grid min-h-screen place-items-center p-6 text-center text-forest-700">Prices couldn&apos;t be loaded just now. Please refresh in a moment.</div>;
  if (!cfg) return <div className="grid min-h-screen place-items-center text-forest-700">Loading prices…</div>;

  const open = foundingOpen(cfg);
  const features = featuresOf(cfg);
  const { plans, addOns } = cfg;
  const ai = addOns.aiTests;
  const toggle = (b) => { setBilling(b); track("billing_toggled", { billing: b }); };
  const pick = (k) => track("plan_selected", { plan: k, billing });
  const priceCell = (k) => {
    const p = plans[k];
    return open ? `${money(p.monthly)} / month or ${money(p.foundingYearly)} / year (founding)` : `${money(p.monthly)} / month or ${money(p.yearly)} / year`;
  };
  const ROWS = [
    ["Read Qur'an and Tajweed", [true, true, true, true]],
    ["Understand Qur'an Course 1", [true, true, true, true]],
    ["Basic Hifz with self-test", [true, true, true, true]],
    ["Hifz: Juz 'Amma", [false, true, true, true]],
    ["Hifz: Important Surahs and Surah Al-Baqarah", [false, false, true, true]],
    ["Understanding Surah Al-Baqarah · Juz 1", [false, false, "Coming soon", "Coming soon"]],
    ["AI Hifz tests", [false, `${ai.includedPerMonth} a month`, `${ai.includedPerMonth} a month`, `${ai.includedPerMonth} a month each`]],
    ["Children Poems and Stories", ["First 2 and 4", `Poems 3–7, ${addOns.stories.includedPerYear} stories a year`, false, true]],
    ["Quran Fluency and 85% Words", ["Free year", false, true, true]],
    ["Learners", ["1", "1", "1", `Up to ${plans.family.maxMembers}`]],
  ];
  const FAQ = [
    ["Why are 'Adn and Firdaws one plan?", "They are the same learning at the same price. We simply start you in 'Adn (ages 14–21) or Firdaws (21 and over) by age, so one plan follows a learner for life."],
    ["Can my child read more than 40 stories a year?", `Yes. From the child's profile you can add more stories, ${addOns.stories.step} at a time at ${money(addOns.stories.pricePerStory)} each, up to ${addOns.stories.maxPerYear} a year.`],
    ["What extras can I add, and when?", "Checkout is just your plan. After you join, add extras from the Add-ons tab in your profile, one at a time. Each one follows your plan's monthly or yearly billing."],
    ["What is the founding family price?", open
      ? `Start a yearly plan by ${foundingEndText(cfg)} and pay ${money(plans.naim.foundingYearly)} (Na'im or 'Adn · Firdaws) or ${money(plans.family.foundingYearly)} (Dar as-Salam) instead of ${money(plans.naim.yearly)} or ${money(plans.family.yearly)}. It stays locked while your subscription stays active, and we email you 7 days before every renewal.`
      : `Yearly plans started by ${foundingEndText(cfg)} keep their founding price while the subscription stays active. New yearly plans are now ${money(plans.naim.yearly)} (Na'im or 'Adn · Firdaws) and ${money(plans.family.yearly)} (Dar as-Salam).`],
    ["Why Course 1 before Hifz?", "Course 1 (Salah) teaches the meaning of what you will memorise, so Hifz starts with understanding. Juz 'Amma opens as soon as Course 1 is done."],
    ["Can I switch between monthly and yearly?", "Yes, from your Billing tab. The change starts at your next renewal and nothing is charged now. Switching to monthly ends the founding price, because monthly prices are never discounted."],
  ];

  return (
    <div className="min-h-screen geo-bg text-foreground" data-testid="pricing-page">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-8">
        <a href="/" className="inline-flex min-h-[44px] items-center font-display text-lg font-semibold text-forest-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500">HikmahVerses</a>
        <nav className="flex items-center gap-4 text-sm font-semibold text-forest-700"><a href="/login" className="inline-flex min-h-[44px] items-center hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500">Sign in</a></nav>
      </header>

      <section className="mx-auto max-w-6xl px-4 pb-12 pt-6 md:px-8">
        <div className="text-center">
          <p className={`${eyebrow} text-gold-600`}>Simple family pricing</p>
          <h1 className="mt-2 font-display text-4xl font-semibold text-forest-700 md:text-5xl">A plan for every chapter.</h1>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <PriceMotionStyle /><BillingToggle billing={billing} onChange={toggle} testId={(b) => `pricing-${b}`} />
            {open && <span className="text-sm font-semibold text-forest-700" data-testid="founding-note">Founding price on yearly plans · join by {foundingEndText(cfg)}</span>}
          </div>
        </div>
        <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {["noor", "naim", "adult", "family"].map((k) => <PlanCard key={k} k={k} cfg={cfg} billing={billing} features={features} onPick={pick} />)}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-12 md:px-8" data-testid="extras-list">
        <h2 className="font-display text-2xl font-semibold text-forest-700">Extras</h2>
        <p className="mt-1 max-w-2xl text-sm text-foreground/70">Add any of these from your profile after you join, whenever you need them. Each one follows your plan&apos;s monthly or yearly billing.</p>
        <dl className="mt-5 divide-y divide-sand-300 rounded-2xl border border-sand-300 bg-white">
          {[
            [addOns.irtaqi.name, `${money(addOns.irtaqi.monthly)} a month or ${money(addOns.irtaqi.yearly)} a year (${money(addOns.irtaqi.familyMonthly)} or ${money(addOns.irtaqi.familyYearly)} on Dar as-Salam)`],
            [addOns.living.name, `${money(addOns.living.monthly)} a month or ${money(addOns.living.yearly)} a year`],
            ["More stories", `${addOns.stories.step} at a time, ${money(addOns.stories.pricePerStory)} a story, up to ${addOns.stories.maxPerYear} a year`],
            ["Extra AI tests", `${ai.packSize} more for ${money(ai.packPrice)}, up to ${ai.maxPacksPerMonth} packs a month`],
            ["Live Learning", `Priced per course. ${cfg.diploma.name}: ${money(cfg.diploma.price)} for ${cfg.diploma.months} months`],
          ].map(([name, text]) => (
            <div key={name} className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-center sm:justify-between"><dt className="font-semibold text-forest-700">{name}</dt><dd className="text-sm text-foreground/75">{text}</dd></div>))}
        </dl>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-12 md:px-8" data-testid="compare">
        <h2 className="font-display text-2xl font-semibold text-forest-700">Compare the plans</h2>
        <div className="relative mt-5 overflow-x-auto rounded-2xl border border-sand-300 bg-white" role="region" aria-label="Plan comparison table" tabIndex={0}>
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="bg-[#EEF5F0] text-xs text-foreground/70">
              <th scope="col" className="px-4 py-3 text-left"><span className="sr-only">Feature</span></th>
              {["noor", "naim", "adult", "family"].map((k) => <th key={k} scope="col" className="px-3 py-3 text-center font-bold text-forest-700">{plans[k].name}</th>)}
            </tr></thead>
            <tbody>
              {ROWS.map(([label, cells]) => (
                <tr key={label} className="border-t border-sand-300"><th scope="row" className="px-4 py-2.5 text-left font-medium">{label}</th>{cells.map((c, i) => <td key={i} className="px-3 py-2.5 text-center"><Mark v={c} /></td>)}</tr>))}
              <tr className="border-t border-sand-300 bg-sand-50" data-testid="compare-price"><th scope="row" className="px-4 py-3 text-left font-semibold">Price</th>
                <td className="px-3 py-3 text-center text-xs">Free, then {money(plans.noor.renewYearly)} a year</td>
                {["naim", "adult", "family"].map((k) => <td key={k} className="px-3 py-3 text-center text-xs">{priceCell(k)}</td>)}</tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-12 md:px-8" data-testid="faq">
        <h2 className="font-display text-2xl font-semibold text-forest-700">Questions</h2>
        <div className="mt-5 divide-y divide-sand-300 rounded-2xl border border-sand-300 bg-white px-5">
          {FAQ.map(([q, a]) => (
            <details key={q} className="group py-4">
              <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-3 font-semibold text-forest-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-500">
                {q}<ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden="true" />
              </summary>
              <p className="mt-2 text-sm leading-relaxed text-foreground/75">{a}</p>
            </details>))}
        </div>
      </section>

      <section className="bg-forest-700 px-4 py-12 text-center text-sand-100">
        <h2 className="font-display text-3xl font-semibold">Learn. Understand. Memorize. Live.</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm text-sand-100/80">HikmahVerses stays free to begin because people sponsor it. If you can, support it for {money(cfg.supportAppeal.monthly)} a month.</p>
        <a href="/login?mode=register" className="mt-6 inline-flex min-h-[44px] items-center gap-2 rounded-[10px] bg-[#DEF5E5] px-6 py-3 text-sm font-semibold text-forest-700 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400">Start Free <ArrowRight className="h-4 w-4" aria-hidden="true" /></a>
      </section>

      <footer className="px-4 py-6 text-center text-xs text-foreground/65" data-testid="pricing-footer">
        Prices in {cfg.currency}, before local taxes. Founding prices are for yearly plans started by {foundingEndText(cfg)}.
      </footer>
    </div>
  );
}
